import { call, delay, put } from 'redux-saga/effects';
import processError from '../../../utils/processError';
import { createExportFailed, createExportSuccessful, loadReportsList } from '../actions';
import { resolveActionPromise } from '../../../utils/resolveActionPromise';
import makeRequest, { getBodyJson } from '../../../utils/makeRequest';
import { rejectActionPromise } from '../../../utils/rejectActionPromise';
import { CREATED, OK } from '../../../configs/serverStatusCodes';
import { BACKEND_URL } from '../../../configs';

const READY_STATUS = 'READY';
const FAILED_STATUS = 'FAILED';
const POLL_DELAY_MS = 5000;
const MAX_POLL_ATTEMPTS = 120;

const buildReportPayload = (pool = {}, fields = []) => {
  const startDate = pool.start_date || pool.created_at_from;
  const endDate = pool.end_date || pool.created_at_to;

  return {
    start_date: startDate,
    end_date: endDate,
    fields,
  };
};

export default function* exportReports(action) {
  try {
    const fieldsUrl = `${BACKEND_URL}/proxy/report/v1/report_fields`;
    const fieldsResponse = yield makeRequest(fieldsUrl, {}, action, 'GET', true);
    const fieldsBody = yield getBodyJson(fieldsResponse, { data: [] });

    if (fieldsResponse.status !== OK || !Array.isArray(fieldsBody?.data) || fieldsBody.data.length === 0) {
      const errorPayload = fieldsBody?.message
        ? fieldsBody
        : { message: 'Не удалось получить поля отчета' };

      yield put(createExportFailed(errorPayload));
      yield call(rejectActionPromise(action, errorPayload));
      return;
    }

    const reportPayload = buildReportPayload(action.payload?.pool, fieldsBody.data);
    if (!reportPayload.start_date || !reportPayload.end_date) {
      const errorPayload = { message: 'Не заданы даты для формирования отчета' };
      yield put(createExportFailed(errorPayload));
      yield call(rejectActionPromise(action, errorPayload));
      return;
    }

    const createUrl = `${BACKEND_URL}/proxy/report/v1/reports`;
    const createResponse = yield makeRequest(createUrl, reportPayload, action, 'POST', true);
    const createBody = yield getBodyJson(createResponse, {});

    if (createResponse.status !== CREATED) {
      yield put(createExportFailed(createBody));
      yield call(rejectActionPromise(action, createBody));
      return;
    }

    const reportId = createBody?.data?.id;
    if (!reportId) {
      const errorPayload = { message: 'Сервис отчетов вернул некорректный ответ' };
      yield put(createExportFailed(errorPayload));
      yield call(rejectActionPromise(action, errorPayload));
      return;
    }

    const statusUrl = `${BACKEND_URL}/proxy/report/v1/reports/${reportId}`;
    let finalReport = null;

    for (let index = 0; index < MAX_POLL_ATTEMPTS; index += 1) {
      const statusResponse = yield makeRequest(statusUrl, {}, action, 'GET', true);
      const statusBody = yield getBodyJson(statusResponse, {});
      const report = statusBody?.data;

      if (statusResponse.status === OK && report?.status === READY_STATUS) {
        finalReport = report;
        break;
      }

      if (statusResponse.status === OK && report?.status === FAILED_STATUS) {
        const errorPayload = { message: 'Формирование отчета завершилось с ошибкой' };
        yield put(createExportFailed(errorPayload));
        yield call(rejectActionPromise(action, errorPayload));
        return;
      }

      yield delay(POLL_DELAY_MS);
    }

    if (!finalReport) {
      const errorPayload = { message: 'Превышено время ожидания формирования отчета' };
      yield put(createExportFailed(errorPayload));
      yield call(rejectActionPromise(action, errorPayload));
      return;
    }

    const successPayload = {
      report: finalReport,
      task: createBody?.data || null,
    };

    yield put(createExportSuccessful(successPayload));
    yield put(loadReportsList({ page: 1 }));
    yield call(resolveActionPromise(action, successPayload));
  } catch (e) {
    yield put(createExportFailed());
    yield call(rejectActionPromise(action, e));
    yield processError(e);
  }
}
